import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as yup from 'yup';
import newslitLogo from '../../../assets/images/newslit-logo.svg';
import rocketIllustration from '../../../assets/images/rocket-illustration.png';
import ErrorReporter from '../../../components/error-reporter/ErrorReporter';
import TwitterLoginBtn from '../../../components/twitter-login-btn/TwitterLoginBtn';
import Input from '../../../components/ui/Input';
import { useAuthFlow, useSession } from '../../../state/app-state';
import DefaultPageLayout from '../../../layouts/defaultPageLayout';
import { finalizeSignInWithTwitter, requestSignUp } from '../../../hooks/useAuth';
import { isLocalStorageAvailable } from '../../../services/authentication';

const SignUpPage: React.FC = () => {
  const history = useHistory();
  const session = useSession();
  const authFlow = useAuthFlow();
  const { search: searchLocation } = useLocation();

  // Query Params
  const searchParams = new URLSearchParams(searchLocation);
  const urlEmail = searchParams.get('email');

  // TODO: Control via SiteFlag
  const showTwitterLogin = false;

  const [legalAccepted, setLegalAccepted] = useState(true);

  useEffect(() => {
    // if account is already logged in, redirect them to the dashboard page
    if (session?.account && session?.token) {
      history.push('/dashboard');
    }
  }, [session, history]);

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: urlEmail || '',
      password: '',
      legal: true,
    },
    validationSchema: yup.object().shape({
      firstName: yup.string().required('First Name is required'),
      lastName: yup.string().required('Last Name is required'),
      email: yup.string().email().required('Email is required'),
      password: yup.string().min(6).max(32).required('Password is required'),
      legal: yup.boolean().isTrue().required(),
    }),
    onSubmit: values => {
      requestSignUp({
        firstName: values.firstName,
        lastName: values.lastName,
        emailAddress: values.email,
        password: values.password,
      });
    },
  });

  return (
    <DefaultPageLayout title="Sign up for Newslit" addClearbitRisk={true}>
      <div className="min-h-screen bg-white flex">
        <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            <div>
              <img className="h-12 w-auto" src={newslitLogo} alt="Newslit" />
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Get started for free</h2>
              <p className="mt-2 text-sm text-gray-600">
                Already have an account?{' '}
                <a
                  href="/users/signin"
                  className="font-medium text-secondary-dark hover:text-secondary"
                >
                  Sign in here.
                </a>
              </p>
            </div>

            <div className="mt-8">
              {showTwitterLogin && (
                <>
                  <div>
                    <div className="mt-1">
                      <TwitterLoginBtn
                        label="Sign up with Twitter"
                        disabled={true}
                        checkIfEnable={() => isLocalStorageAvailable()}
                        onSuccess={() => finalizeSignInWithTwitter()}
                      />
                    </div>
                  </div>

                  <div className="mt-6 relative">
                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                      <div className="w-full border-t border-gray-300" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">Or continue with Email</span>
                    </div>
                  </div>
                </>
              )}

              <div className="mt-6">
                <form
                  onSubmit={formik.handleSubmit}
                  className="grid grid-cols-1 gap-y-6 gap-x-4 grid-cols-6"
                >
                  <div className="col-span-3">
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                      First name
                    </label>
                    <div className="mt-1">
                      <Input
                        type="text"
                        name="firstName"
                        id="firstName"
                        required
                        autoComplete="given-name"
                        error={
                          formik.touched.firstName && formik.errors.firstName
                            ? formik.errors.firstName
                            : undefined
                        }
                        value={formik.values.firstName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </div>

                  <div className="col-span-3">
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                      Last name
                    </label>
                    <div className="mt-1">
                      <Input
                        type="text"
                        name="lastName"
                        id="lastName"
                        required
                        autoComplete="family-name"
                        error={
                          formik.touched.lastName && formik.errors.lastName
                            ? formik.errors.lastName
                            : undefined
                        }
                        value={formik.values.lastName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </div>

                  <div className="col-span-6">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email address
                    </label>
                    <div className="mt-1">
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        error={
                          formik.touched.email && formik.errors.email
                            ? formik.errors.email
                            : undefined
                        }
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </div>

                  <div className="col-span-6">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <div className="mt-1">
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        required
                        error={
                          formik.touched.password && formik.errors.password
                            ? formik.errors.password
                            : undefined
                        }
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </div>

                  <div className="col-span-6">
                    <div className="flex">
                      <input
                        id="remember-me"
                        name="remember-me"
                        type="checkbox"
                        checked={legalAccepted}
                        onChange={(e: any) => {
                          const value = e.target.checked;
                          setLegalAccepted(value);
                        }}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded-sm"
                      />
                      <label
                        htmlFor="remember-me"
                        className="ml-2 block text-sm text-gray-900 font-normal m-0"
                      >
                        I agree to the{' '}
                        <a
                          rel="noopener noreferrer"
                          target="_blank"
                          className="font-bold text-secondary-dark hover:text-secondary"
                          href="https://newslit.co/terms"
                        >
                          terms of service
                        </a>{' '}
                        and{' '}
                        <a
                          rel="noopener noreferrer"
                          target="_blank"
                          className="font-bold text-secondary-dark hover:text-secondary"
                          href="https://newslit.co/privacy"
                        >
                          privacy policy
                        </a>
                      </label>
                    </div>
                  </div>

                  <div className="col-span-6">
                    <button
                      type="submit"
                      disabled={authFlow.isFetching || !formik.isValid || !legalAccepted}
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-secondary hover:bg-secondary-dark focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary disabled:opacity-50"
                    >
                      {authFlow.isFetching && (
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      )}
                      Start Free Trial
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div className="hidden lg:block relative w-0 flex-1">
          <img
            className="absolute inset-0 h-full w-full object-cover"
            src={rocketIllustration}
            alt="Newslit Illustration"
          />
        </div>
      </div>

      <ErrorReporter />
    </DefaultPageLayout>
  );
};

export default SignUpPage;
